"use client";

// L'Orchestrateur : l'agent de l'entreprise toujours là (api/app/objectif.py). Il a son visage, une grue à tour au
// jaune de chantier : au travail, son chariot court sur la flèche et monte une brique ; en veille, elle respire et
// laisse filer des « z » ; en pause, elle passe au gris. Sa carte dit ce qu'il fait, ou quand il se réveille.

import { useCallback, useEffect, useState } from "react";
import {
  api,
  ErreurApi,
  EVENEMENTS,
  type AgentDuProjet,
  type Evenement,
  type Orchestrateur,
} from "@/lib/api";
import "./orchestrateur.css";

export type EtatGrue = Orchestrateur["etat"];

export function Grue({
  etat = "veille",
  className = "",
}: {
  etat?: EtatGrue;
  className?: string;
}) {
  return (
    <svg
      className={`grue ${etat} ${className}`}
      viewBox="0 0 36 32"
      aria-hidden
    >
      {/* le mât en treillis, sur son socle */}
      <g className="grue-structure">
        <rect x="9" y="10" width="4" height="19" rx="0.6" />
        <path className="grue-treillis" d="M9 13 13 17 9 21 13 25 9 29" />
        <rect x="6" y="29" width="10" height="2.4" rx="1" />
        {/* le pylône, ses haubans, la flèche et la contre-flèche */}
        <path className="grue-haubans" d="M3.5 8 11 2.5 34 8" />
        <rect x="2.5" y="7.2" width="32" height="1.8" rx="0.6" />
        <rect
          className="grue-lest"
          x="2.6"
          y="9"
          width="4.4"
          height="3.2"
          rx="0.6"
        />
        <rect
          className="grue-cabine"
          x="8.2"
          y="9"
          width="5.6"
          height="3.4"
          rx="0.8"
        />
      </g>
      {/* le chariot, son câble et la brique qu'il monte */}
      <g className="grue-chariot">
        <rect
          className="grue-structure"
          x="25"
          y="8.6"
          width="4"
          height="1.6"
          rx="0.4"
        />
        <rect className="grue-cable" x="26.7" y="10" width="0.6" height="9" />
        <g className="grue-charge">
          <path className="grue-crochet" d="M27 19v1.4a1.1 1.1 0 1 1-1.4-1" />
          <rect
            className="grue-brique"
            x="24"
            y="21.4"
            width="6"
            height="3.2"
            rx="0.9"
          />
        </g>
      </g>
      <g className="grue-z">
        <text x="18" y="18">
          z
        </text>
        <text x="21" y="14">
          z
        </text>
      </g>
    </svg>
  );
}

// « dans 7 min », « dans 40 s » : le temps jusqu'au prochain réveil.
function dans(iso: string, maintenant: number): string {
  const s = Math.round((new Date(iso).getTime() - maintenant) / 1000);
  if (s <= 5) return "imminent";
  if (s < 60) return `dans ${s} s`;
  return `dans ${Math.round(s / 60)} min`;
}

export function ligneEtat(o: Orchestrateur, maintenant: number): string {
  if (o.etat === "en-pause") return "en pause : il ne lance plus aucun tour";
  if (o.etat === "au-travail") {
    const [premier, ...autres] = o.au_travail;
    return `au travail · ${premier.mission}${autres.length ? ` (+${autres.length} projet${autres.length > 1 ? "s" : ""})` : ""}`;
  }
  return o.prochain_reveil
    ? `en veille · réveil ${dans(o.prochain_reveil, maintenant)}`
    : "en veille";
}

// L'état de l'orchestrateur, en direct : relu à chaque nouvelle qui le concerne, et une horloge pour le compte à rebours.
export function useOrchestrateur(onErreur?: (message: string) => void) {
  const [o, setO] = useState<Orchestrateur | null>(null);
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const charger = useCallback(async () => {
    try {
      setO(await api.orchestrateur());
    } catch {
      // Une API d'avant l'orchestrateur : la page fait sans.
    }
  }, []);

  useEffect(() => {
    void charger();
    let attente: number | undefined;
    const flux = new EventSource(EVENEMENTS);
    flux.onmessage = (m) => {
      const e = JSON.parse(m.data) as Evenement;
      if (e.type === "orchestrateur" || e.type === "agent") {
        window.clearTimeout(attente);
        attente = window.setTimeout(() => void charger(), 300);
      }
    };
    const horloge = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => {
      flux.close();
      window.clearTimeout(attente);
      window.clearInterval(horloge);
    };
  }, [charger]);

  const agir = useCallback(
    async (action: () => Promise<Orchestrateur>) => {
      try {
        setO(await action());
      } catch (e) {
        onErreur?.(e instanceof ErreurApi ? e.message : "L'API ne répond pas.");
      }
    },
    [onErreur],
  );
  const reveiller = useCallback(() => agir(api.reveiller), [agir]);
  const activer = useCallback(
    (actif: boolean) => agir(() => api.activer(actif)),
    [agir],
  );

  return { o, maintenant, reveiller, activer };
}

// Sa place en tête des agents : épinglé, sans croix pour le supprimer.
export function CarteAgentOrchestrateur({
  o,
  maintenant,
  occupe,
}: {
  o: Orchestrateur;
  maintenant: number;
  occupe: boolean; // son fil répond à quelqu'un en ce moment
}) {
  // Dans la liste, ce qu'il fait ; son état, la grue le montre, et l'infobulle (et l'en-tête du chat) le dit.
  return (
    <span
      className="orch-carte"
      title={occupe ? "vous répond" : ligneEtat(o, maintenant)}
    >
      <Grue etat={occupe ? "au-travail" : o.etat} />
      <span className="orch-carte-texte">
        <span className="orch-nom">{o.nom}</span>
        <span className="orch-ligne">Surveille l&apos;avancement</span>
      </span>
    </span>
  );
}

// L'en-tête du chat quand son fil est ouvert : son état, et de quoi le réveiller ou le mettre en pause.
export function EnteteOrchestrateur({
  o,
  maintenant,
  onReveiller,
  onActiver,
}: {
  o: Orchestrateur;
  maintenant: number;
  onReveiller: () => Promise<void>;
  onActiver: (actif: boolean) => Promise<void>;
}) {
  return (
    <span className="orch-entete">
      <Grue etat={o.etat} />
      <strong>{o.nom}</strong>
      <span className="orch-ligne">{ligneEtat(o, maintenant)}</span>
      <span className="orch-commandes">
        <button
          type="button"
          className="bouton-petit"
          disabled={!o.actif}
          onClick={() => void onReveiller()}
          title="Il reprend tout de suite chaque projet qui n'est ni prêt, ni en pause, ni bloqué sans changement"
        >
          Réveiller
        </button>
        <button
          type="button"
          className="bouton-petit discret"
          onClick={() => void onActiver(!o.actif)}
        >
          {o.actif ? "Pause" : "Reprendre"}
        </button>
      </span>
    </span>
  );
}

// L'agent du projet : toujours là, en tête des chats du projet, il ne tourne que quand on lui parle (l'orchestrateur
// ou vous). Il a la même grue ; sa ligne dit où en est le projet pour lui.
const grueDuProjet = (a: AgentDuProjet, occupe: boolean): EtatGrue =>
  occupe || a.etat === "en-cours"
    ? "au-travail"
    : a.etat === "en-pause"
      ? "en-pause"
      : "veille";

export function ligneAgentProjet(a: AgentDuProjet, occupe: boolean): string {
  if (a.etat === "en-cours" && a.mission)
    return `au travail · ${a.mission.nom}`;
  if (occupe) return "vous répond";
  if (a.etat === "pret") return "mémoire technique prêt";
  if (a.etat === "no-go") return "no-go : le projet s'arrête là";
  if (a.etat === "en-pause") return "en pause : le Conducteur le laisse";
  if (a.decision === "a-prendre") return "attend la décision go / no-go";
  if (a.etat === "bloque") return "attend l'entreprise";
  return a.mission ? `prochaine étape · ${a.mission.nom}` : "en veille";
}

export function CarteAgentProjet({
  a,
  occupe,
}: {
  a: AgentDuProjet;
  occupe: boolean;
}) {
  const grue = grueDuProjet(a, occupe);
  return (
    <span className="orch-carte" title={ligneAgentProjet(a, occupe)}>
      <Grue etat={grue} />
      <span className="orch-carte-texte">
        <span className="orch-nom">{a.nom}</span>
        <span className="orch-ligne">Mène le projet</span>
      </span>
    </span>
  );
}

// Son en-tête, son fil ouvert : où en est le projet, et de quoi le relancer ou le mettre en pause.
export function EnteteAgentProjet({
  a,
  onRelancer,
  onPause,
}: {
  a: AgentDuProjet;
  onRelancer: () => Promise<void>;
  onPause: (pause: boolean) => Promise<void>;
}) {
  const fini = a.etat === "pret" || a.etat === "no-go";
  return (
    <span className="orch-entete">
      <Grue etat={grueDuProjet(a, a.occupe)} />
      <strong>{a.nom}</strong>
      <span className="orch-ligne">{ligneAgentProjet(a, a.occupe)}</span>
      {!fini && (
        <span className="orch-commandes">
          <button
            type="button"
            className="bouton-petit"
            disabled={
              a.etat === "en-cours" ||
              a.etat === "en-pause" ||
              a.decision === "a-prendre"
            }
            onClick={() => void onRelancer()}
            title="Le Conducteur lui confie tout de suite l'étape suivante de la pipeline"
          >
            Relancer
          </button>
          <button
            type="button"
            className="bouton-petit discret"
            onClick={() => void onPause(a.etat !== "en-pause")}
          >
            {a.etat === "en-pause" ? "Reprendre" : "Pause"}
          </button>
        </span>
      )}
    </span>
  );
}

// L'agent du projet, en direct : relu à chaque nouvelle du projet ou de l'orchestrateur.
export function useAgentDuProjet(
  affaire: string,
  onErreur?: (message: string) => void,
) {
  const [a, setA] = useState<AgentDuProjet | null>(null);

  const charger = useCallback(async () => {
    try {
      setA(await api.agentDuProjet(affaire));
    } catch {
      // Une API d'avant l'agent du projet : la page fait sans.
    }
  }, [affaire]);

  useEffect(() => {
    void charger();
    let attente: number | undefined;
    const flux = new EventSource(EVENEMENTS);
    flux.onmessage = (m) => {
      const e = JSON.parse(m.data) as Evenement;
      const ici =
        e.type === "orchestrateur" ||
        (e.type === "document" && e.id === affaire) ||
        ((e.type === "agent" || e.type === "decision") &&
          e.affaire === affaire);
      if (ici) {
        window.clearTimeout(attente);
        attente = window.setTimeout(() => void charger(), 300);
      }
    };
    return () => {
      flux.close();
      window.clearTimeout(attente);
    };
  }, [affaire, charger]);

  const agir = useCallback(
    async (action: () => Promise<unknown>) => {
      try {
        await action();
        await charger();
      } catch (e) {
        onErreur?.(e instanceof ErreurApi ? e.message : "L'API ne répond pas.");
      }
    },
    [charger, onErreur],
  );
  const relancer = useCallback(
    () => agir(() => api.relancer(affaire)),
    [agir, affaire],
  );
  const pause = useCallback(
    (p: boolean) => agir(() => api.pauseProjet(affaire, p)),
    [agir, affaire],
  );

  return { a, relancer, pause };
}
